import type React from 'react'
import { useMemo } from 'react'
import { Table } from '@/components/ui/table'
import { cn } from '@/lib/cn'
import { fmtMoney } from './format'
import type { IncomeStatementResponse } from '@/features/reporting/types'

export interface IncomeStatementDrillArgs {
  mainCategory: string
  subcategory: string
  endDate: string
  startDate?: string
}

interface Props {
  data: IncomeStatementResponse
  tableRef?: React.MutableRefObject<HTMLTableElement | null>
  onDrillDown?: (args: IncomeStatementDrillArgs) => void
}

export function IncomeStatementReport({ data, tableRef, onDrillDown }: Props) {
  const cols = data.columns

  const opexCats = useMemo(() => {
    const s = new Set<string>()
    for (const c of cols)
      for (const cat of Object.keys(c.categories)) {
        if (!['Revenue', 'Cost of Goods Sold', 'Other Income', 'Other Expenses'].includes(cat)) {
          s.add(cat)
        }
      }
    return [...s].sort()
  }, [cols])

  function sectionRows(name: string, key: string): React.ReactNode[] {
    const subs = new Set<string>()
    for (const c of cols) if (c.categories[key]) Object.keys(c.categories[key]).forEach((x) => subs.add(x))
    if (subs.size === 0) return []
    const sorted = [...subs].sort()
    const rows: React.ReactNode[] = [
      <tr key={`cat-${name}`} className="bg-muted/40 font-semibold">
        <td colSpan={cols.length + 1} className="px-3 py-1 pl-6">
          {name}
        </td>
      </tr>,
    ]
    for (const sub of sorted) {
      rows.push(
        <tr key={`${name}-${sub}`} className="border-t">
          <td className="px-3 py-1.5 pl-8">{sub}</td>
          {cols.map((c, idx) => {
            const clickable = !!onDrillDown
            return (
              <td
                key={idx}
                className={cn(
                  'px-3 py-1.5 text-right',
                  clickable && 'cursor-pointer text-primary hover:underline',
                )}
                onClick={
                  clickable
                    ? () =>
                        onDrillDown({
                          mainCategory: key,
                          subcategory: sub,
                          endDate: c.endDate,
                          startDate: c.startDate,
                        })
                    : undefined
                }
              >
                {fmtMoney(c.categories[key]?.[sub] ?? 0)}
              </td>
            )
          })}
        </tr>,
      )
    }
    return rows
  }

  function totalRow(
    label: string,
    key: keyof typeof cols[number]['totals'],
    grand = false,
  ): React.ReactNode {
    return (
      <tr
        key={`total-${label}`}
        className={cn('border-t font-semibold', grand ? 'bg-muted font-bold' : 'bg-muted/30')}
      >
        <td className="px-3 py-1.5">{label}</td>
        {cols.map((c, i) => (
          <td key={i} className="px-3 py-1.5 text-right">
            {fmtMoney(c.totals[key])}
          </td>
        ))}
      </tr>
    )
  }

  return (
    <div className="overflow-x-auto rounded-md border">
      <Table className="w-full min-w-[640px] text-sm" ref={tableRef}>
        <thead className="bg-primary-gradient text-xs uppercase tracking-wide text-primary-foreground">
          <tr>
            <th className="px-3 py-2 text-left">Account</th>
            {cols.map((c) => (
              <th key={c.label} className="px-3 py-2 text-right">
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sectionRows('Revenue', 'Revenue')}
          {totalRow('Total Revenue', 'totalRevenue')}
          {sectionRows('Cost of Goods Sold', 'Cost of Goods Sold')}
          {totalRow('Total COGS', 'totalCOGS' as keyof typeof cols[number]['totals'])}
          {totalRow('Gross Profit', 'grossProfit' as keyof typeof cols[number]['totals'], true)}
          {opexCats.length > 0 ? (
            <>
              <tr className="bg-muted/70 font-bold">
                <td colSpan={cols.length + 1} className="px-3 py-1.5">
                  Operating Expenses
                </td>
              </tr>
              {opexCats.flatMap((c) => sectionRows(c, c))}
              {totalRow('Total Operating Expenses', 'totalExpenses' as keyof typeof cols[number]['totals'])}
              {totalRow('Operating Income', 'operatingIncome' as keyof typeof cols[number]['totals'], true)}
            </>
          ) : null}
          {sectionRows('Other Income', 'Other Income')}
          {sectionRows('Other Expenses', 'Other Expenses')}
          {totalRow('NET INCOME', 'netIncome', true)}
        </tbody>
      </Table>
    </div>
  )
}
